<!--
SubscriptionTestClockTab — body of the "Relógio" tab in the staff
SubscriptionDetailModal (S11 · C4).

Gate (checked by the PARENT modal before this component is even mounted, per
GatewayTestDevTrigger's own gate — see its file header): the subscription's
provider must be 'gatewaytest' AND the gateway feature map must report
gatewaytest ENABLED. This component does not re-check either — it trusts the
tab it lives in.

ONE dial, on purpose (user decision, 2026-08-07): "avançar N dias". No target-
date form, no boundary shortcuts. The advance is PERMANENT and CUMULATIVE —
there is no rollback — so it goes through the same confirm-box idiom the modal
already uses for Cancelar, with its own irreversibility warning.

State lives in useTestClock (module-scope singleton) so the tab label's offset
badge in the parent modal reads the same numbers this body renders — one
fetch, two readers.
-->
<template>
  <div data-testid="subscription-test-clock-tab">
    <p class="test-clock-intro">
      {{ t('Avança as datas desta assinatura de teste — permanente e cumulativo, sem volta.') }}
    </p>

    <div class="test-clock-offset-row">
      <span>{{ t('Deslocamento acumulado') }}:</span>
      <span data-testid="test-clock-offset" class="test-clock-offset-value">
        {{ offsetDaysTotal > 0 ? `+${offsetDaysTotal}d` : t('Nenhum') }}
      </span>
    </div>

    <inline-message v-if="error" severity="error" data-testid="test-clock-error">
      {{ t('Não foi possível carregar o estado do relógio') }}: {{ error }}
    </inline-message>

    <h4 class="test-clock-section-title">{{ t('Avançar o relógio') }}</h4>
    <div class="test-clock-form">
      <input-number
        v-model="days"
        :label="t('Dias a avançar')"
        :min="1"
        :max="3650"
        :disabled="confirming || isAdvancing"
        elem-id="test-clock-days-input"
        data-testid="test-clock-days"
      />
      <app-button
        :label="t('Avançar')"
        icon="pi pi-forward"
        severity="primary"
        :disabled="!isValidDays || confirming || isAdvancing"
        data-testid="test-clock-advance"
        @click="confirming = true"
      />
    </div>

    <box
      v-if="confirming"
      type="danger"
      icon="pi pi-exclamation-triangle"
      :title="t('Confirmar avanço')"
      elem-id="boxTestClockConfirm"
    >
      <div data-testid="test-clock-confirm-box">
        <!--
          `days` is concatenated OUTSIDE t(): a `{days}` placeholder inside an
          UNREGISTERED key reaches the user literally (see
          SubscriptionDetailModal's handleProcess for the same trap, hit once
          already in this domain).
        -->
        <p class="confirm-text">
          {{ t('Desloca as datas em') }} {{ days }} {{ t('dias e emite os webhooks das fronteiras cruzadas. Não há volta.') }}
        </p>
        <div class="actions-section">
          <app-button
            :label="t('Confirmar avanço')"
            icon="pi pi-forward"
            severity="danger"
            size="small"
            :disabled="isAdvancing"
            data-testid="test-clock-confirm"
            @click="handleAdvance"
          />
          <app-button
            :label="t('Voltar')"
            severity="secondary"
            outlined
            size="small"
            :disabled="isAdvancing"
            data-testid="test-clock-abort"
            @click="confirming = false"
          />
        </div>
      </div>
    </box>

    <!--
      Chronology is per-outcome coloured, which CompactDataTable's single
      value column cannot do (one CSS class for the whole list) — a small
      flex list instead, never a hand-rolled HTML grid.
    -->
    <div v-if="chronologyDisplayRows.length > 0" class="test-clock-chronology-section">
      <h4 class="test-clock-section-title">{{ t('Cronologia do último avanço') }}</h4>
      <div data-testid="test-clock-chronology" class="test-clock-chronology-list">
        <div
          v-for="row in chronologyDisplayRows"
          :key="row.key"
          class="test-clock-chronology-row"
          :data-testid="row.testId"
        >
          <span :class="['test-clock-tone-badge', `test-clock-tone-${row.tone}`]">{{ row.label }}</span>
          <span class="test-clock-chronology-detail">{{ row.detail }}</span>
        </div>
      </div>
    </div>

    <div class="test-clock-history-section">
      <h4 class="test-clock-section-title">{{ t('Histórico de avanços') }}</h4>
      <div data-testid="test-clock-history">
        <compact-data-table :value="historyRows" prop-column-width="200px" value-column-width="auto" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../eventBus'
import { log } from '../../utils/DebugLogger'
import { useTestClock } from '../../composables/useTestClock'
import { useDateFormatter } from '../../composables/useDateFormatter'
import Box from '../../ui/Box.vue'
import AppButton from '../../ui/AppButton.vue'
import InlineMessage from '../../ui/InlineMessage.vue'
import CompactDataTable from '../../grid/CompactDataTable.vue'

const props = defineProps<{
  subscriptionId: number | null
}>()

const emit = defineEmits<{
  (e: 'refresh'): void
}>()

const { t } = useI18n()
const { offsetDaysTotal, advances, lastChronology, error, advance, fetchState } = useTestClock()
const { fromApiDateTimeFormat, formatDateTime } = useDateFormatter()

const EMPTY = '—'

const days = ref<number | null>(null)
const confirming = ref(false)
const isAdvancing = ref(false)

const isValidDays = computed((): boolean =>
  days.value != null && Number.isInteger(days.value) && days.value >= 1 && days.value <= 3650,
)

// ── Chronology display (S11 · C2/C3): webhook rows first, then job rows ────

type ChronologyTone = 'ok' | 'warn' | 'fail'

interface ChronologyDisplayRow {
  key: string
  label: string
  detail: string
  tone: ChronologyTone
  testId: string
}

function chronologyTone(outcome: string | undefined): ChronologyTone {
  if (!outcome) return 'warn'
  if (outcome.startsWith('failed')) return 'fail'
  if (outcome === 'processed' || outcome === 'already_processed' || outcome === 'success') return 'ok'
  return 'warn'
}

function webhookOutcomeLabel(outcome: string | undefined): string {
  if (!outcome) return EMPTY
  const labels: Record<string, string> = {
    processed: t('Processado'),
    already_processed: t('Já processado (repetição ignorada)'),
    ignored: t('Ignorado'),
  }
  if (labels[outcome]) return labels[outcome]
  if (outcome === 'skipped:never_confirm') return t('Não emitido — simulador nunca confirma')
  if (outcome.startsWith('failed:')) return `${t('Falhou')}: ${outcome.slice('failed:'.length)}`
  return outcome
}

function jobOutcomeLabel(outcome: string | undefined): string {
  if (!outcome) return EMPTY
  const labels: Record<string, string> = {
    success: t('Sucesso'),
    failed: t('Falhou'),
    skipped: t('Ignorado'),
  }
  if (labels[outcome]) return labels[outcome]
  if (outcome === 'skipped:job_not_seeded') return t('Ignorado — job não configurado')
  if (outcome === 'skipped:job_disabled') return t('Ignorado — job desativado')
  return outcome
}

const chronologyDisplayRows = computed((): ChronologyDisplayRow[] =>
  lastChronology.value.map((row, idx) => {
    const testId = `test-clock-chronology-row-${idx}`
    if (row.kind === 'job') {
      return {
        key: testId,
        label: row.job_type ?? t('Job'),
        detail: `${jobOutcomeLabel(row.outcome)}${row.run_id != null ? ` · run ${row.run_id}` : ''}`,
        tone: chronologyTone(row.outcome),
        testId,
      }
    }
    const truncatedId = row.event_id ? row.event_id.slice(0, 12) : EMPTY
    return {
      key: testId,
      label: row.event_type ?? t('Webhook'),
      detail: `${webhookOutcomeLabel(row.outcome)} · ${truncatedId}`,
      tone: chronologyTone(row.outcome),
      testId,
    }
  }),
)

// ── History: CompactDataTable of every recorded advance, newest first ──────

const historyRows = computed(() =>
  advances.value.map((a, idx) => ({
    prop: a.created_at ? formatDateTime(fromApiDateTimeFormat(a.created_at)) : EMPTY,
    // `days`/offset are concatenated OUTSIDE t() — see the confirm text above.
    value: `${a.advanced_by ?? EMPTY} · ${a.days ?? 0} ${t('dias')} · ${t('offset')}: +${a.offset_days_after ?? 0}d`,
    testId: `test-clock-history-row-${idx}`,
  })),
)

// ── Advance action ───────────────────────────────────────────────────────────

const ADVANCE_MESSAGES: Record<string, string> = {
  test_clock_real_gateway: 'Este gateway não é o simulador de testes — o relógio não se aplica aqui.',
  subscription_not_found: 'Assinatura não encontrada.',
}

function advanceErrorMessage(reason: string): string {
  const known = ADVANCE_MESSAGES[reason]
  if (known) return t(known)
  return `${t('Não foi possível avançar o relógio')}: ${reason}`
}

async function handleAdvance(): Promise<void> {
  const id = props.subscriptionId
  if (id == null || !isValidDays.value) return

  isAdvancing.value = true
  try {
    await advance(id, days.value as number)
    // The advance response carries only offset + chronology; the full
    // history list (this advance's own row, "quem/quando") is the server's
    // record — re-read it rather than assemble it client-side.
    await fetchState(id)
    log('SubscriptionTestClockTab', 'handleAdvance', 'Relógio avançado', { subscriptionId: id, days: days.value })
    eventBus.emit('globalMessage.success', t('Relógio avançado'))
    confirming.value = false
    days.value = null
    emit('refresh')
  } catch (err) {
    const reason = (err as Error)?.message ?? ''
    log('SubscriptionTestClockTab', 'handleAdvance', 'Avanço não concluído', { subscriptionId: id, reason })
    eventBus.emit('globalMessage.error', advanceErrorMessage(reason))
    confirming.value = false
  } finally {
    isAdvancing.value = false
  }
}
</script>

<style scoped>
.test-clock-intro {
  margin: 0 0 0.75rem;
  color: var(--p-text-muted-color);
}

.test-clock-offset-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
}

.test-clock-offset-value {
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  background-color: var(--p-primary-100, #dbeafe);
  color: var(--p-primary-700, #1d4ed8);
}

.test-clock-form {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
}

/* The house input wrapper carries margin-bottom: 1rem for form stacking;
   inside this single-row flex it pushes the BUTTON 1rem below the input's
   baseline (flex-end aligns margin boxes). Kill it here so input and button
   share the same bottom line. */
.test-clock-form :deep(.input-number-wrapper) {
  margin-bottom: 0;
}

.actions-section {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.75rem;
}

.confirm-text {
  margin: 0 0 0.75rem;
}

.test-clock-section-title {
  margin: 1.25rem 0 0.5rem;
}

.test-clock-chronology-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.test-clock-chronology-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
}

.test-clock-tone-badge {
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.test-clock-tone-ok {
  background-color: var(--p-green-100);
  color: var(--p-green-700);
}

.test-clock-tone-warn {
  background-color: var(--p-yellow-100);
  color: var(--p-yellow-700);
}

.test-clock-tone-fail {
  background-color: var(--p-red-100);
  color: var(--p-red-700);
}
</style>
